// frontend/src/hooks/useCryptoTokenRiverVisualizer.js
import { useEffect, useState } from "react";
import { creerLecteurContinu } from "../utils/continuationPlayback";

/**
 * Flux de la Rivière de Tokens — REÇU du backend, jamais redéfini ici.
 *
 * Voir docs/DOCTRINE_GEOMETRIE_CANONIQUE.md et docs/PROCEDURE_CONFORMITE.md.
 *
 * ⚠️ Ce hook faisait quatre choses, dont trois qu'il n'aurait pas dû faire :
 *
 *   - il récupérait les frames de `/animate` … que la page ne transmettait
 *     JAMAIS au composant. Les tokens du backend n'atteignaient donc jamais
 *     l'affichage ; la rivière visible était intégralement inventée par le
 *     front, et le résultat de la requête était jeté ;
 *   - il calculait une entropie dynamique locale à partir de
 *     `Math.random() * 10`, de bruit et d'une oscillation, pondérée par les
 *     curseurs ;
 *   - il remontait `shannon: Math.random() * tokenCount` — une entropie de
 *     Shannon littéralement remplacée par un tirage ;
 *   - il envoyait à `/dyn_entropy` des champs `turbulence`, `collisions` et
 *     `drift` tirés au sort, que la route ignorait.
 *
 * Il ne fait plus qu'une chose : demander la forme du canal et la séquence de
 * frames. Les curseurs `density` et `river_speed` sont transmis au MOTEUR, où
 * ils règlent le débit réel de la source (règle 2).
 */
export default function useCryptoTokenRiverVisualizer({
  riverSpeedPct = 100,
  densityPct = 100,
  steps = 200,
} = {}) {
  const [flux, setFlux] = useState({
    canal: null,
    alphabet: 0,
    borneTheorique: 0,
    lecteur: null,
    chargement: true,
  });

  useEffect(() => {
    const controller = new AbortController();
    const json = (r) =>
      r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`));

    setFlux((f) => ({ ...f, chargement: true }));

    fetch("/api/geometry/crypto_token_river/initial", { signal: controller.signal })
      .then(json)
      .then((data) => {
        const params = new URLSearchParams({
          steps: String(steps),
          dt: "0.03",
          density: String(Math.max(densityPct, 2) / 100),
          river_speed: String(Math.max(riverSpeedPct, 10) / 100),
        });

        // ⚠️ LECTURE CONTINUE — le rejeu d'un segment unique téléportait le
        // flux à la couture : 165x le pas typique.
        //
        // L'appariement par IDENTIFIANT reste entier : le lecteur ne livre que
        // des frames, et c'est le composant qui apparie par `id`. Le contrat du
        // module prime sur le helper (docs/SPEC_CONTINUATION.md § 6.2).
        const lecteur = creerLecteurContinu({
          url: "/api/geometry/crypto_token_river/animate",
          parametres: () => Object.fromEntries(params),
        });
        lecteur.demarrer();
        setFlux({
          canal: data.canal,
          alphabet: data.alphabet,
          borneTheorique: data.borne_theorique_bits,
          lecteur,
          chargement: false,
        });
        return null;
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Erreur flux crypto_token_river:", err);
          setFlux((f) => ({ ...f, chargement: false }));
        }
      });

    return () => {
      controller.abort();
      setFlux((precedent) => {
        if (precedent.lecteur) precedent.lecteur.arreter();
        return { ...precedent, lecteur: null };
      });
    };
  }, [riverSpeedPct, densityPct, steps]);

  return flux;
}
